import type { PublicConfig } from '@/api/types';
import type { AppThemeOptions, FontKey } from '@/theme/theme';
import { FONT_STACKS } from '@/theme/theme';

/** Theme options derived from the tenant's branding (empty on the platform console). */
export function themeOptionsFor(config: PublicConfig): AppThemeOptions {
  const branding = config.branding;
  if (!branding) return {};
  const tokens = (branding.tokens ?? {}) as { radius?: unknown; font?: unknown };
  const font =
    typeof tokens.font === 'string' && tokens.font in FONT_STACKS ? (tokens.font as FontKey) : null;
  return {
    primary: branding.primaryColor,
    secondary: branding.secondaryColor ?? null,
    radius: typeof tokens.radius === 'number' ? tokens.radius : null,
    font,
  };
}

function setLink(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.removeAttribute('type');
  link.href = href;
}

/**
 * Applies what lives outside React: tab title, favicon, home-screen icon and
 * the browser UI color. Runs on load and after brand edits.
 */
export function applyDocumentBranding(config: PublicConfig, primaryMain: string) {
  const name = config.tenant?.displayName ?? config.platformName;
  document.title = name;
  const branding = config.branding;
  const icon = branding?.faviconUrl ?? branding?.iconUrl ?? branding?.logoUrl;
  if (icon) {
    setLink('icon', icon);
    setLink('apple-touch-icon', branding?.iconUrl ?? icon);
  }
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"][media*="light"]')
    .forEach((meta) => meta.setAttribute('content', primaryMain));
  const description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) description.content = branding?.tagline ?? name;
}
